import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlowNav } from "../components/FlowNav";
import {
  getObservationOrder,
  getRequiredModulesBeforeResources,
  resolveConcernContactNow,
  resolveConcernFromAnswers,
  resolveConcernInfoOnly,
  resolveConcernPossibleMinor,
  resolveConcernSkipAll,
} from "../concern/resolveBystander";
import type { ConcernView } from "../concern/types";
import type { IndicatorAnswer, ResolvedRouting } from "../help/types";
import { useSession } from "../state/SessionProvider";

/**
 * Concerned About Someone — observe, don't investigate.
 * Observations stay in React memory only. Never in URLs or storage.
 */
export function ConcernPage() {
  const navigate = useNavigate();
  const {
    translate,
    concernAnswers,
    setConcernAnswer,
    clearConcernAnswers,
    setResourceCategories,
    setResourceReturnPath,
    concernBookmark,
    setConcernBookmark,
    clearQrContext,
  } = useSession();

  const [view, setView] = useState<ConcernView>("hub");
  const [history, setHistory] = useState<ConcernView[]>([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [activeModules, setActiveModules] = useState<string[]>([]);
  const [optionsRouting, setOptionsRouting] = useState<ResolvedRouting | null>(
    null,
  );
  const [pendingResourceCategories, setPendingResourceCategories] = useState<
    string[] | null
  >(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const restored = useRef(false);

  const observationOrder = getObservationOrder();

  useEffect(() => {
    if (restored.current || !concernBookmark) return;
    restored.current = true;
    setView(concernBookmark.view as ConcernView);
    setQuestionIndex(concernBookmark.questionIndex);
    setHistory(concernBookmark.history as ConcernView[]);
    setConcernBookmark(null);
  }, [concernBookmark, setConcernBookmark]);

  useEffect(() => {
    headingRef.current?.focus();
  }, [view, questionIndex]);

  function pushView(next: ConcernView) {
    setHistory((h) => [...h, view]);
    setView(next);
  }

  function goBack() {
    if (view === "observations" && questionIndex > 0) {
      setQuestionIndex((i) => i - 1);
      return;
    }
    const prev = history[history.length - 1] ?? "hub";
    setHistory((h) => h.slice(0, -1));
    setView(prev);
  }

  function clearConcernFlow() {
    clearConcernAnswers();
    setOptionsRouting(null);
    setActiveModules([]);
    setPendingResourceCategories(null);
    setHistory([]);
    setQuestionIndex(0);
    setView("hub");
    setConcernBookmark(null);
    setResourceCategories(null);
  }

  /** Mode hub ← Back → / and clears this mode’s transient state + QR context. */
  function goBackToLanding() {
    clearConcernFlow();
    clearQrContext();
    navigate("/");
  }

  /** Nested Home → / and clears this mode’s transient state + QR context. */
  function goHome() {
    clearConcernFlow();
    clearQrContext();
    navigate("/");
  }

  function goTalk(categories: string[]) {
    setPendingResourceCategories(categories);
    setActiveModules(getRequiredModulesBeforeResources());
    pushView("gate");
  }

  function continueToResources() {
    const cats =
      pendingResourceCategories ??
      resolveConcernContactNow().resourceCategories;
    setConcernBookmark({
      view,
      questionIndex,
      history,
    });
    setResourceReturnPath("/concern");
    setResourceCategories(cats);
    navigate("/resources");
  }

  function goOptions(routing: ResolvedRouting) {
    setOptionsRouting(routing);
    pushView("options");
  }

  function openModules(modules: string[]) {
    setActiveModules(modules);
    pushView("modules");
  }

  function startObservations() {
    setQuestionIndex(0);
    pushView("observations");
  }

  function recordAndAdvance(id: string, value: IndicatorAnswer) {
    setConcernAnswer(id, value);
    const nextAnswers = { ...concernAnswers, [id]: value };
    if (questionIndex >= observationOrder.length - 1) {
      goOptions(resolveConcernFromAnswers(nextAnswers));
      return;
    }
    setQuestionIndex((i) => i + 1);
  }

  function stopQuestions() {
    clearConcernAnswers();
    goOptions(resolveConcernSkipAll());
  }

  const routing = optionsRouting ?? resolveConcernFromAnswers(concernAnswers);
  const nestedNav = <FlowNav onBack={goBack} onHome={goHome} />;

  function observePromptKey(indicatorId: string): string {
    return `concern.observe.${indicatorId}`;
  }

  if (view === "unsure") {
    return (
      <section className="stack help-flow" aria-labelledby="concern-heading">
        <h1
          id="concern-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("page.concern.title")}
        </h1>
        <p>{translate("mode.concern.unsure.prompt")}</p>
        <p className="muted sme-note">{translate("mode.concern.sme_note")}</p>
        <div className="stack">
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => openModules(resolveConcernInfoOnly().modules)}
          >
            <span className="mode-card-label">
              {translate("mode.concern.unsure.learn.label")}
            </span>
            <span className="mode-card-blurb">
              {translate("mode.concern.unsure.learn.blurb")}
            </span>
          </button>
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => pushView("educate")}
          >
            <span className="mode-card-label">
              {translate("mode.concern.unsure.observe.label")}
            </span>
            <span className="mode-card-blurb">
              {translate("mode.concern.unsure.observe.blurb")}
            </span>
          </button>
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => goOptions(resolveConcernInfoOnly())}
          >
            <span className="mode-card-label">
              {translate("mode.concern.unsure.safe.label")}
            </span>
          </button>
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => goTalk(resolveConcernContactNow().resourceCategories)}
          >
            <span className="mode-card-label">
              {translate("mode.concern.unsure.talk.label")}
            </span>
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  if (view === "educate") {
    return (
      <section className="stack help-flow" aria-labelledby="concern-heading">
        <h1
          id="concern-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("mode.concern.educate.heading")}
        </h1>
        <p className="muted sme-note">{translate("mode.concern.sme_note")}</p>

        <article className="info-module">
          <h2>{translate("module.observation_vs_assumption.title")}</h2>
          <p>{translate("module.observation_vs_assumption.body")}</p>
          <p>
            <strong>{translate("mode.concern.educate.observation_label")}</strong>{" "}
            {translate("mode.concern.educate.observation_example")}
          </p>
          <p>
            <strong>{translate("mode.concern.educate.assumption_label")}</strong>{" "}
            {translate("mode.concern.educate.assumption_example")}
          </p>
          <p>{translate("mode.concern.educate.assumption_limit")}</p>
        </article>

        <article className="info-module">
          <h2>{translate("module.bystander_do_nots.title")}</h2>
          <p>{translate("module.bystander_do_nots.intro")}</p>
          <ul className="do-not-list">
            <li>{translate("mode.concern.donot.confront_controller")}</li>
            <li>{translate("mode.concern.donot.confront_person")}</li>
            <li>{translate("mode.concern.donot.follow")}</li>
            <li>{translate("mode.concern.donot.photograph")}</li>
            <li>{translate("mode.concern.donot.record")}</li>
            <li>{translate("mode.concern.donot.search")}</li>
            <li>{translate("mode.concern.donot.detain")}</li>
            <li>{translate("mode.concern.donot.rescue")}</li>
            <li>{translate("mode.concern.donot.social")}</li>
            <li>{translate("mode.concern.donot.danger")}</li>
          </ul>
        </article>

        <div className="stack">
          <button
            type="button"
            className="mode-link mode-link--quiet"
            onClick={startObservations}
          >
            {translate("mode.concern.educate.continue_observe")}
          </button>
          <button
            type="button"
            className="mode-link mode-link--quiet"
            onClick={() => goOptions(resolveConcernInfoOnly())}
          >
            {translate("answer.see_safe_options")}
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  if (view === "observations") {
    const indicatorId = observationOrder[questionIndex];
    const liveRouting = resolveConcernFromAnswers(concernAnswers);
    const talkCategories =
      liveRouting.resourceCategories.length > 0
        ? liveRouting.resourceCategories
        : resolveConcernContactNow().resourceCategories;

    return (
      <section className="stack help-flow" aria-labelledby="concern-heading">
        <h1
          id="concern-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("mode.concern.observe.orientation")}
        </h1>
        <p className="muted">{translate("mode.concern.observe.helper")}</p>
        <p className="muted sme-note">{translate("mode.concern.sme_note")}</p>

        <div className="question-block">
          <h2 className="question-prompt">
            {translate(observePromptKey(indicatorId))}
          </h2>
          <div
            className="answer-grid"
            role="group"
            aria-label={translate(observePromptKey(indicatorId))}
          >
            <button
              type="button"
              className="mode-link mode-link--quiet"
              onClick={() => recordAndAdvance(indicatorId, "yes")}
            >
              {translate("answer.yes")}
            </button>
            <button
              type="button"
              className="mode-link mode-link--quiet"
              onClick={() => recordAndAdvance(indicatorId, "no")}
            >
              {translate("answer.no")}
            </button>
            <button
              type="button"
              className="mode-link mode-link--quiet"
              onClick={() => recordAndAdvance(indicatorId, "not_sure")}
            >
              {translate("answer.not_sure")}
            </button>
            <button
              type="button"
              className="mode-link mode-link--quiet"
              onClick={() => recordAndAdvance(indicatorId, "prefer_not")}
            >
              {translate("answer.prefer_not")}
            </button>
          </div>
        </div>

        <div className="stack escape-actions">
          <button
            type="button"
            className="text-action"
            onClick={() => goOptions(liveRouting)}
          >
            {translate("answer.see_safe_options")}
          </button>
          <button
            type="button"
            className="text-action"
            onClick={() => goTalk(talkCategories)}
          >
            {translate("answer.talk_to_someone")}
          </button>
          <button type="button" className="text-action" onClick={stopQuestions}>
            {translate("answer.skip_all")}
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  if (view === "gate") {
    return (
      <section className="stack help-flow" aria-labelledby="concern-heading">
        <h1
          id="concern-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("mode.concern.gate.heading")}
        </h1>
        <p className="muted sme-note">{translate("mode.concern.sme_note")}</p>
        {activeModules.map((moduleId) => (
          <article key={moduleId} className="info-module">
            <h2>{translate(`${moduleId}.title`)}</h2>
            <p>{translate(`${moduleId}.body`)}</p>
          </article>
        ))}
        <div className="stack">
          <button
            type="button"
            className="mode-link mode-link--quiet"
            onClick={continueToResources}
          >
            {translate("mode.concern.gate.continue")}
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  if (view === "modules") {
    const modules =
      activeModules.length > 0
        ? activeModules
        : resolveConcernInfoOnly().modules;
    return (
      <section className="stack help-flow" aria-labelledby="concern-heading">
        <h1
          id="concern-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("mode.concern.modules.heading")}
        </h1>
        <p className="muted sme-note">{translate("mode.concern.sme_note")}</p>
        {modules.map((moduleId) => (
          <article key={moduleId} className="info-module">
            <h2>{translate(`${moduleId}.title`)}</h2>
            <p>{translate(`${moduleId}.body`)}</p>
          </article>
        ))}
        <div className="stack">
          <button
            type="button"
            className="mode-link mode-link--quiet"
            onClick={() => goOptions(resolveConcernInfoOnly())}
          >
            {translate("answer.see_safe_options")}
          </button>
          <button
            type="button"
            className="mode-link mode-link--quiet"
            onClick={() =>
              goTalk(
                pendingResourceCategories ??
                  resolveConcernContactNow().resourceCategories,
              )
            }
          >
            {translate("answer.talk_to_someone")}
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  if (view === "options") {
    const framingKey = routing.hasAnyYes
      ? "mode.concern.options_framing"
      : "mode.concern.options_framing_generic";
    return (
      <section className="stack help-flow" aria-labelledby="concern-heading">
        <h1
          id="concern-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("mode.concern.options.heading")}
        </h1>
        <p>{translate(framingKey)}</p>
        <p className="muted sme-note">{translate("mode.concern.sme_note")}</p>
        <div className="stack">
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => openModules(routing.modules)}
          >
            <span className="mode-card-label">
              {translate("mode.concern.options.learn")}
            </span>
          </button>
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => goTalk(routing.resourceCategories)}
          >
            <span className="mode-card-label">
              {translate("mode.concern.options.talk")}
            </span>
          </button>
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => pushView("educate")}
          >
            <span className="mode-card-label">
              {translate("mode.concern.options.observe")}
            </span>
          </button>
          <button
            type="button"
            className="text-action"
            onClick={() => {
              const minor = resolveConcernPossibleMinor();
              setPendingResourceCategories(minor.resourceCategories);
              openModules(minor.modules);
            }}
          >
            {translate("mode.concern.options.possible_minor")}
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  return (
    <section className="stack help-flow" aria-labelledby="concern-heading">
      <h1
        id="concern-heading"
        className="help-heading"
        ref={headingRef}
        tabIndex={-1}
      >
        {translate("page.concern.title")}
      </h1>
      <p className="muted">{translate("mode.concern.awareness")}</p>
      <p className="muted">{translate("mode.concern.awareness.disclosure")}</p>
      <p className="muted sme-note">{translate("mode.concern.sme_note")}</p>

      <h2 className="mode-heading">{translate("mode.concern.hub.prompt")}</h2>

      <div className="stack">
        <button
          type="button"
          className="mode-card mode-link--quiet"
          onClick={() => pushView("educate")}
        >
          <span className="mode-card-label">
            {translate("mode.concern.hub.understand.label")}
          </span>
          <span className="mode-card-blurb">
            {translate("mode.concern.hub.understand.blurb")}
          </span>
        </button>

        <button
          type="button"
          className="mode-card mode-link--quiet"
          onClick={() => {
            clearConcernAnswers();
            goOptions(resolveConcernInfoOnly());
          }}
        >
          <span className="mode-card-label">
            {translate("mode.concern.hub.safe.label")}
          </span>
          <span className="mode-card-blurb">
            {translate("mode.concern.hub.safe.blurb")}
          </span>
        </button>

        <button
          type="button"
          className="mode-card mode-link--quiet"
          onClick={() => goTalk(resolveConcernContactNow().resourceCategories)}
        >
          <span className="mode-card-label">
            {translate("mode.concern.hub.talk.label")}
          </span>
          <span className="mode-card-blurb">
            {translate("mode.concern.hub.talk.blurb")}
          </span>
        </button>

        <button
          type="button"
          className="mode-card mode-link--quiet"
          onClick={() => pushView("unsure")}
        >
          <span className="mode-card-label">
            {translate("mode.concern.hub.unsure.label")}
          </span>
          <span className="mode-card-blurb">
            {translate("mode.concern.hub.unsure.blurb")}
          </span>
        </button>
      </div>

      <FlowNav onBack={goBackToLanding} />
    </section>
  );
}
